import { Box, ButtonBase, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { Block } from "../../shared/Block";

interface InviteCardProps {
  referralLink: string;
  onOpenQr: () => void;
  onShare: () => void;
}

export function InviteCard({
  referralLink,
  onOpenQr,
  onShare,
}: InviteCardProps) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation("referral");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* fallback — Telegram browser */
    }
  };

  return (
    <Block sx={{ marginTop: "10px", padding: "14px 16px" }}>
      {/* Заголовок */}
      <Box>
        <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
          {t("invite.card_title")}
        </Typography>
        <Typography sx={{ fontSize: "11px", opacity: 0.55, marginTop: "1px" }}>
          {t("invite.card_subtitle")}
        </Typography>
      </Box>

      {/* QR-миниатюра + ссылка */}
      <Box
        sx={{
          marginTop: "14px",
          display: "flex",
          alignItems: "stretch",
          gap: "10px",
        }}
      >
        <ButtonBase
          onClick={onOpenQr}
          sx={{
            flexShrink: 0,
            width: 78,
            padding: "6px",
            borderRadius: "14px",
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:active": { transform: "scale(0.97)" },
          }}
        >
          <QRCodeSVG
            value={referralLink}
            size={66}
            bgColor="#FFFFFF"
            fgColor="#000000"
            level="M"
            includeMargin={false}
          />
        </ButtonBase>

        <ButtonBase
          onClick={handleCopy}
          sx={{
            flex: 1,
            minWidth: 0,
            padding: "10px 12px",
            borderRadius: "14px",
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            color: "text.primary",
            "&:active": { transform: "scale(0.995)" },
          }}
        >
          <Box sx={{ minWidth: 0, textAlign: "left" }}>
            <Typography
              sx={{ fontSize: "11px", opacity: 0.55, letterSpacing: "0.4px" }}
            >
              {t("invite.link_uppercase")}
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                marginTop: "2px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={referralLink}
            >
              {referralLink}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 34,
              height: 34,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: copied
                ? "rgba(58,167,109,0.15)"
                : (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(51,119,255,0.14)"
                      : "rgba(51,119,255,0.10)",
              color: copied ? "#3AA76D" : "primary.main",
              transition: "background-color 0.2s ease, color 0.2s ease",
            }}
          >
            <Icon
              icon={copied ? "solar:check-circle-bold" : "solar:copy-bold"}
              width={18}
              height={18}
            />
          </Box>
        </ButtonBase>
      </Box>

      {/* CTA */}
      <ButtonBase
        onClick={onShare}
        sx={{
          width: "100%",
          marginTop: "12px",
          padding: "12px 16px",
          borderRadius: "14px",
          backgroundColor: "primary.main",
          color: "#FFFFFF",
          fontWeight: 600,
          fontSize: "14.5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          "&:active": { transform: "scale(0.98)" },
        }}
      >
        <Icon icon="solar:paper-plane-bold" width={17} height={17} />
        {t("invite.share_telegram")}
      </ButtonBase>
    </Block>
  );
}
